'use client'

import { useState } from 'react'
import clsx from 'clsx'
import type { FiftyFiftySectionFragment } from '@/lib/types/datocms/graphql/generated'
import Image from 'next/image'
import { StructuredText } from 'react-datocms'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import CtaDefault from '../ui/cta/CtaDefault'
import CircleGraphic from '../ui/CircleGraphic'
import ArrowButton from '../ui/ArrowButton'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function FiftyFiftySection({
	features,
	theme,
}: Readonly<FiftyFiftySectionFragment>) {
	const [activeIndex, setActiveIndex] = useState(0)
	const feature = features[activeIndex]

	if (!features.length) return null

	const handleNext = () => {
		setActiveIndex((prevIndex) => (prevIndex + 1) % features.length)
	}

	const handlePrev = () => {
		setActiveIndex((prevIndex) => (prevIndex - 1 + features.length) % features.length)
	}

	return (
		<section className="fifty-fifty-section @container">
			<div
				className={clsx(
					'relative common-x-padding py-3 @xl:py-10 @3xl:py-16 @5xl:py-32 overflow-hidden',
					{
						light: 'bg-white',
						dark: 'bg-blue-dark',
						basic: 'bg-white',
					}[theme ?? 'dark']
				)}
			>
				<div
					className={clsx(
						'relative flex flex-col-reverse @5xl:grid @5xl:grid-cols-2 max-w-screen-2xl mx-auto z-10 border-blue',
						theme !== 'basic' &&
							{
								left: '@5xl:border-l-8',
								right: '@5xl:border-r-8',
							}[feature.accentBarOrientation ?? 'left'],
						theme === 'basic' ? 'justify-between gap-x-20 w-full' : 'border-t-8 @5xl:border-t-0',
						feature.orientation === 'left' ? '' : '2xl:flex-row-reverse',
						theme === 'light' ? 'bg-white-frosted' : 'bg-white'
					)}
				>
					{/* Text Side */}
					<div
						key={feature.id as string}
						className={clsx(
							'relative flex flex-col justify-center gap-4 @5xl:gap-10  py-12  w-full',
							theme === 'basic' ? '@5xl:max-w-lg @5xl:mx-auto' : 'px-6 @5xl:p-16 @7xl:p-24',
							feature.orientation === 'left' ? 'order-2 @5xl:order-1' : 'order-1 @5xl:order-2'
						)}
					>
						{/* Eyebrow */}
						{feature.tag ? <Eyebrow>{feature.tag}</Eyebrow> : null}

						{/* Title */}
						{feature.title && !isStructuredTextEmpty(feature.title.text) && (
							<h2
								className={clsx(
									'heading-size-3 text-contrast max-w-2xl',
									features.length > 1 && 'line-clamp-3'
								)}
							>
								<TextWithHighlight text={feature.title} />
							</h2>
						)}

						<hr className="@5xl:hidden my-2 border-contrast/10" />

						{/* Description */}
						{feature.description && !isStructuredTextEmpty(feature.description) && (
							<div
								className={clsx(
									'rich-text text-lg text-contrast/70 @xl:mr-20 @5xl:mr-10 @7xl:mr-0',
									features.length > 1 && 'line-clamp-3'
								)}
							>
								<StructuredText data={feature.description} />
							</div>
						)}

						{/* CTA */}
						{feature.cta ? <CtaDefault cta={feature.cta} className="mt-8 @5xl:mt-0" /> : null}

						{/* Previous/next arrows (desktop) */}
						{features.length > 1 ? (
							<div className="hidden @3xl:block absolute bottom-0 right-0">
								<ArrowButton
									onClick={handleNext}
									direction="right"
									theme={theme}
									aria-label="Next"
								/>
								<ArrowButton onClick={handlePrev} theme={theme} aria-label="Previous" />
							</div>
						) : null}
					</div>

					{/* Image Side */}
					<div
						className={clsx(
							'relative flex flex-col h-full w-full overflow-hidden @5xl:aspect-square bg-gray-light',
							theme === 'basic' && 'rounded-xl',
							feature.orientation === 'left' ? 'order-2 @5xl:order-1' : 'order-2 @5xl:order-1'
						)}
					>
						{/* Image */}
						{feature.image?.[0]?.url ? (
							<Image
								src={feature.image?.[0]?.url}
								alt={feature.image?.[0]?.alt ?? `Feature Image ${feature.id + 1}`}
								width={feature.image?.[0]?.width}
								height={feature.image?.[0]?.height}
								className="h-full w-full object-cover"
								sizes="(max-width: 1024px) 90vw, 45vw"
							/>
						) : null}

						{feature.gridIronEffect && (
							<div className="h-[80px] @5xl:h-[195px] w-full bg-blue-dark overflow-hidden">
								<div className="relative mx-auto overflow-hidden">
									<div className="flex flex-wrap gap-4 @5xl:gap-3 @5xl:gap-y-12shrink-0 w-[120%] translate-x-1 translate-y-2 @5xl:translate-y-2.5 @5xl:translate-x-3">
										{Array.from({ length: 50 }).map((_, index) => (
											<CircleGraphic
												key={`shape-${index + 1}`}
												size="sm"
												theme="blue"
												className="shrink-0"
											/>
										))}
									</div>
								</div>
							</div>
						)}

						{/* Previous/next arrows (mobile) */}
						{features.length > 1 ? (
							<div className="@3xl:hidden absolute bottom-0 right-0">
								<ArrowButton
									onClick={handleNext}
									direction="right"
									theme={theme}
									aria-label="Next"
								/>
								<ArrowButton onClick={handlePrev} theme={theme} aria-label="Previous" />
							</div>
						) : null}
					</div>
				</div>

				{/* Background pattern */}
				{theme === 'dark' && (
					<div className="hidden @5xl:grid grid-cols-3 xl:grid-cols-3 2xl:grid-cols-5 gap-20 @6xl:gap-16 @7xl:gap-10 absolute inset-0 scale-125 pointer-events-none">
						{Array.from({ length: 30 }).map((_, i) => (
							<CircleGraphic key={`shape-${i + 1}`} theme="dark" size="fill" />
						))}
					</div>
				)}
			</div>
		</section>
	)
}
